import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Output,
} from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import { NgbDateParserFormatter } from "@ng-bootstrap/ng-bootstrap";
import { PlacementArray } from "@ng-bootstrap/ng-bootstrap/util/positioning";
import { BrMaskModel } from "brmasker-ionic-3/dist/directives/brmasker-ionic-3";
import { NgbCustomParserFormatter } from "components/common/date-picker/ngb-custom-parser-formatter";
import moment from "moment";
import { commonVar } from "providers/common-var/common";
import { Observable, Subscription } from "rxjs";
import { map } from "rxjs/operators";
import { PermissionsResponse } from "../../../../app/access-control/permissions-response";
import ResourceManagementLookups from "../../../../models/common/resource-management-lookups";
import { ContactType } from "../../../../models/company-resources/contact-type";
import CompanyResourceType from "../../../../models/resource-management/company-resource-type.enum";
import CredentialedResource from "../../../../models/resource-management/credentialed-resource";
import ProfessionalProvider from "../../../../models/resource-management/professional-provider";
import { ContactDetails } from "../../../../pages/company-resources/contact-details/contact-details";
import { ResourceManagementService } from "../../../../providers/resource-management/resource-management.service";

const EMPLOYEE = 2;

@Component({
  selector: "workforce-job-role-details",
  templateUrl: "workforce-job-role-details.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => WorkforceJobRoleDetails),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => WorkforceJobRoleDetails),
      multi: true,
    },
    { provide: NgbDateParserFormatter, useClass: NgbCustomParserFormatter },
  ],
})
export class WorkforceJobRoleDetails
  extends ContactDetails
  implements OnDestroy
{
  protected readonly companyResourceType = CompanyResourceType;
  _contactIsEmployee: boolean = false;
  _contactIsSystemUser: boolean = false;
  _resourceType: number = 1;
  npiDuplicated: boolean = false;
  hideAddDiscipline: boolean = false;
  npiShort: boolean = false;
  credDuplicated: boolean = false;
  invalidDate: boolean = false;
  private minDateValue: any = { year: 1900, month: 1, day: 1 };
  dateRange: any;
  providerSubscriptions: Subscription[] = [];
  private _rmLookups: ResourceManagementLookups;

  stateList = commonVar.STATE_LIST;

  credentialsOptions = [];

  volunteerDisciplineOptions = [
    {
      display: "Volunteer",
      value: 14,
    },
  ];
  volunteerTypeOptions = [];

  department = [
    "ADMISSIONS",
    "BEREAVEMENT",
    "BILLING",
    "CLINICAL",
    "COMPLIANCE",
    "FINANCIAL",
    "MARKETING",
    "OPERATIONS",
    "SOCIAL_SERVICES",
    "MEDICAL_RECORDS",
    "SPIRITUAL",
    "TECHNOLOGY",
    "ADMINISTRATION",
    "HUMAN_RESOURCES",
  ];
  position = [
    "AIDE",
    "ACCOUNTANT",
    "ADMINISTRATOR",
    "ASSISTANT_DIRECTOR",
    "CHAPLAIN",
    "COORDINATOR",
    "COUNSELOR",
    "DIRECTOR",
    "EXECUTIVE",
    "LIAISON",
    "MANAGER",
    "NURSE",
    "SOCIAL_WORKER",
    "SUPERVISOR",
    "THERAPIST",
    "MEDICAL_DIRECTOR",
    "BEREAVEMENT",
    "TECHNOLOGY",
    "MARKETING",
    "CLERK",
    "RECEPTIONIST",
    "LEAD",
  ];
  disciplinesName = [
    "LICENSED_NURSE",
    "NURSE_PRACTITIONER",
    "NUTRITIONIST",
    "OCCUPATIONAL_THERAPIST",
    "PHYSICAL_THERAPIST",
    "PHYSICIAN_ASSISTANT",
    "REGISTERED_NURSE",
    "SOCIAL_WORKER",
    "SPEECH_LANGUAGE_THERAPIST",
    "SPIRITUAL_ADVISOR",
    "BEREAVEMENT",
    "DOCTOR_OF_MEDICINE",
    "DOCTOR_OF_OSTEOPATHY",
    "HOSPICE_AIDE",
    "VOLUNTEER",
    "VOLUNTEER_COORDINATOR",
    "NO_DISCIPLINE",
  ];
  employmentStatus: any[] = [];
  employmentStatusOptions: any = ["FULL_TIME", "PART_TIME", "PRN"];
  taxonomy: any = [];
  taxonomyCodeDescription: any = [];
  datePlacementPreference: PlacementArray = ["bottom-left"];
  startDateMin: any = "";
  startDateMax: any = "";
  endDateMin: any = "";
  endDateMax: any = "";
  validFromMin: any = "";
  validFromMax: any = "";
  validToMin = [];
  validToMax: any = "";
  npiBrmasker: BrMaskModel;
  selectorOpen: boolean = false;

  @Input() isEditMode: boolean = true;
  @Input() contactData: CredentialedResource;
  @Output() recheckNPIEvent = new EventEmitter<any>();
  @Output() deleteCredential = new EventEmitter<any>();
  _permissionsAccess = {} as PermissionsResponse;

  @Input()
  set permissionsAccess(permissionsAccess: PermissionsResponse) {
    if (
      permissionsAccess &&
      permissionsAccess.permissions &&
      permissionsAccess.permissions.length > 0
    ) {
      this._permissionsAccess = permissionsAccess;
    }
  }

  get disciplines() {
    if (this.containsControl("disciplines")) {
      return this.getDisciplines() as FormArray;
    }
  }

  private getDisciplines() {
    return this.innerFormGroup.get("disciplines");
  }

  get credentials() {
    return this.innerFormGroup.get("credentials") as FormArray;
  }

  @Input()
  set rmLookups(rmLookups: ResourceManagementLookups) {
    this._rmLookups = rmLookups;
    if (rmLookups && rmLookups.Position && rmLookups.Position.length > 0) {
      this.mapLookups(rmLookups);
    }
  }

  get rmLookups() {
    return this._rmLookups;
  }

  @Input() set contactIsEmployee(value: boolean) {
    this._contactIsEmployee = value ? value : false;
    this._contactIsEmployee
      ? this.addEmployeeAttributes()
      : this.removeEmployeeAttributes();
  }

  get contactIsEmployee() {
    return this._contactIsEmployee;
  }

  @Input() set contactIsSystemUser(value: boolean) {
    this._contactIsSystemUser = value;
  }

  get contactIsSystemUser() {
    return this._contactIsSystemUser;
  }

  @Input() set resourceType(value: number) {
    if (this._resourceType != value) {
      this._resourceType = value;
    }
    this.addCompanyResourceAttributes();
    this.removeCompanyResourceAttributes();
  }

  get resourceType() {
    return this._resourceType;
  }

  private getDeaStartDate() {
    return this.innerFormGroup.get("deaStartDate");
  }

  private getDeaExpireDate() {
    return this.innerFormGroup.get("deaExpireDate");
  }

  private getValidFromDatetime() {
    return this.innerFormGroup.get("validFromDatetime");
  }

  private getValidToDatetime() {
    return this.innerFormGroup.get("validToDatetime");
  }

  private removeControl(name: string) {
    this.innerFormGroup.removeControl(name);
  }

  private containsControl(name: string) {
    return this.innerFormGroup.contains(name);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    super.ngOnDestroy();
  }

  set value(value: any) {
    if (value.credentials) {
      const credentialsCount = value.credentials.length;
      let credentialsControlsCount = this.credentials.controls.length;

      while (credentialsControlsCount !== credentialsCount) {
        this.addCredential(event);
        credentialsControlsCount = this.credentials.controls.length;
      }
    }

    if (value.professionalRelation === EMPLOYEE) {
      value.employmentStatus =
        this.employmentStatusOptions.indexOf(value.employmentStatus) !== -1
          ? this.employmentStatusOptions.indexOf(value.employmentStatus)
          : null;
      this.addEmployeeAttributes();
    }
    if (+value.resourceType === ContactType.VOLUNTEERS && value.volunteerType) {
      this.resourceType = +value.resourceType;
    }

    this.innerFormGroup.patchValue(value);
    this.onChange(value);
    this.onTouched();
  }

  constructor(
    protected formBuilder: FormBuilder,
    protected resourceManagementService: ResourceManagementService
  ) {
    super(formBuilder);

    this.innerFormGroup = this.formBuilder.group({
      credentials: new FormArray([]),
    });

    this.subscriptions.push(
      this.innerFormGroup.valueChanges.subscribe((value) => {
        if (
          this.containsControl("npi") &&
          this.getNpi().dirty &&
          (value.npi || value.npi === "") &&
          value.npi.length < 10
        ) {
          this.npiShort = true;
        }
        this.onChange(value);
        this.onTouched();
      })
    );

    this.subscriptions.push(
      this.credentials.valueChanges.subscribe((value) => {
        let duplicated = false;
        let invalidDateCheck = false;
        let prevCred = {};
        for (let i = 0; i < this.credentials.value.length; i++) {
          let credential = this.credentials.at(i).value;
          if (!credential.deleted) {
            // Set limit for expiration date
            let fromDateValue = credential.validFromDatetime;
            let toDateValue = credential.validToDatetime;
            if (fromDateValue != "" && fromDateValue !== null) {
              let validFromDate = new Date(fromDateValue);
              this.validToMin[i] = {
                year: validFromDate.getFullYear(),
                month: validFromDate.getMonth() + 1,
                day: validFromDate.getDate(),
              };
            }
            //Validate credential date
            if (!toDateValue) {
              toDateValue = "";
            }
            if (toDateValue && toDateValue != "" && toDateValue !== null) {
              let validToDate = new Date(toDateValue);
              if (toDateValue > validToDate) invalidDateCheck = true;
            }
            // Duplicated credential management
            if (
              prevCred[credential.type] &&
              prevCred[credential.type][credential.issueState] &&
              credential.licenseId &&
              prevCred[credential.type][credential.issueState].includes(
                credential.licenseId
              )
            ) {
              duplicated = true;
            } else {
              credential.type = credential.type ? credential.type : 0;
              credential.issueState = credential.issueState
                ? credential.issueState
                : "";
              if (!prevCred[credential.type]) {
                prevCred[credential.type] = {};
              }
              if (!prevCred[credential.type][credential.issueState]) {
                prevCred[credential.type][credential.issueState] = [];
              }
              prevCred[credential.type][credential.issueState].push(
                credential.licenseId
              );
            }
            if (credential.type === 27 && !credential.other) {
              this.credentials
                .at(i)
                .get("other")
                .setValidators([Validators.required]);
            }
          }
        }
        this.credDuplicated = duplicated;
        this.invalidDate = invalidDateCheck;
      })
    );

    this.subscriptions.push(
      // Update Tab status with Async Validation
      this.innerFormGroup.statusChanges.subscribe((value) => {
        if (value === "VALID") {
          this.recheckNPIEvent.emit("");
        }
      })
    );

    let now = new Date();
    const tenYearsLater = {
      year: now.getFullYear() + 10,
      month: now.getMonth() + 1,
      day: now.getDate(),
    };
    const eightyYearsAgo = {
      year: now.getFullYear() - 80,
      month: now.getMonth() + 1,
      day: now.getDate(),
    };

    this.startDateMin = eightyYearsAgo;
    this.startDateMax = tenYearsLater;
    this.endDateMin = eightyYearsAgo;
    this.endDateMax = tenYearsLater;
    this.validFromMin = eightyYearsAgo;
    this.validFromMax = tenYearsLater;
    this.validToMax = tenYearsLater;
    this.npiBrmasker = new BrMaskModel();
    this.npiBrmasker.len = 10;
    this.npiBrmasker.type = "num";
  }

  private mapLookups(rmLookups: ResourceManagementLookups) {
    this.employmentStatus = rmLookups["EmploymentStatus"].map((item) => {
      return {
        display: item.description,
        value: item.id,
      };
    });

    this.credentialsOptions = rmLookups["CredentialType"].map((item) => {
      return {
        display: item.description,
        value: item.id,
      };
    });
    this.volunteerTypeOptions = rmLookups["VolunteerType"].map((item) => {
      return {
        display: item.description,
        value: item.id,
      };
    });
  }

  private getNpi() {
    return this.innerFormGroup.get("npi");
  }

  valid(control: string) {
    return this.innerFormGroup.get(control).valid;
  }

  touched(control: string) {
    return this.innerFormGroup.get(control).touched;
  }

  validCredential(index) {
    return this.credentials.at(index).valid;
  }

  touchedCredential(index) {
    return this.credentials.at(index).touched;
  }

  validate(_: FormControl) {
    return this.innerFormGroup.valid &&
      !this.credDuplicated &&
      !this.invalidDate
      ? null
      : { jobRole: { valid: false } };
  }

  addCredential(event) {
    if (event) {
      event.preventDefault();
    }
    const group = this.formBuilder.group({
      id: [""],
      type: [null, Validators.required],
      other: [null],
      issueState: [null, Validators.required],
      licenseId: [""],
      validFromDatetime: ["", Validators.required],
      validToDatetime: [""],
      operatingAs: [true],
      deleted: [""],
    });

    let now = new Date();
    const today = {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
    };
    this.validToMin.push(today);
    this.credentials.push(group);
  }

  removeCredential(event, index, credential) {
    event.preventDefault();
    if (credential.value["id"] > 0) {
      credential.get("deleted").setValue(true);
      credential.get("type").clearValidators();
      credential.get("licenseId").clearValidators();
      credential.get("issueState").clearValidators();
      credential.get("validFromDatetime").clearValidators();
      credential.get("validToDatetime").clearValidators();
      credential.get("other").clearValidators();
      this.deleteCredential.emit(credential.value);
    }
    this.validToMin.splice(index, 1);
    this.credentials.removeAt(index);
  }

  filterDeletedCredentials(creds: AbstractControl[]): AbstractControl[] {
    return creds.filter(
      (c) => c.value["deleted"] == false || c.value["deleted"] == ""
    );
  }

  addEmployeeAttributes() {
    if (!this.containsControl("employmentStatus")) {
      this.innerFormGroup.addControl(
        "employmentStatus",
        this.formBuilder.control(null)
      );
    }
  }

  removeEmployeeAttributes() {
    if (this.containsControl("employmentStatus")) {
      this.removeControl("employmentStatus");
    }

    if (
      this.resourceType === CompanyResourceType.PROVIDER &&
      this.containsControl("npi")
    ) {
      this.innerFormGroup.controls["npi"].setValue(this.getNpi().value);
    }
  }

  addCompanyResourceAttributes() {
    if (+this.resourceType === CompanyResourceType.PROVIDER) {
      this.addProviderControls();
    } else if (+this.resourceType === CompanyResourceType.VOLUNTEER) {
      this.addVolunteerControls();
    }
  }

  removeCompanyResourceAttributes() {
    if (+this.resourceType !== CompanyResourceType.PROVIDER) {
      this.removeProviderControls();
    }
    if (+this.resourceType !== CompanyResourceType.VOLUNTEER) {
      this.removeVolunteerControls();
    }
  }

  private addVolunteerControls() {
    if (!this.containsControl("volunteerType")) {
      this.innerFormGroup.addControl(
        "volunteerType",
        this.formBuilder.control(null, Validators.required)
      );
    }
    if (!this.containsControl("trainingCompletionDate")) {
      this.innerFormGroup.addControl(
        "trainingCompletionDate",
        this.formBuilder.control("")
      );
    }
  }

  private removeVolunteerControls() {
    if (this.containsControl("volunteerType")) {
      this.removeControl("volunteerType");
    }
    if (this.containsControl("trainingCompletionDate")) {
      this.removeControl("trainingCompletionDate");
    }
  }

  private addProviderControls() {
    if (!this.containsControl("npi")) {
      this.innerFormGroup.addControl(
        "npi",
        this.formBuilder.control(
          "",
          [Validators.required, Validators.pattern("^[0-9]{10}$")],
          this.validateProviderAvailable.bind(this)
        )
      );
    }
    if (!this.containsControl("dea")) {
      this.innerFormGroup.addControl(
        "dea",
        this.formBuilder.control("", [
          Validators.minLength(9),
          Validators.pattern("^[a-zA-Z]{2}[0-9]{7}$"),
        ])
      );
      this.innerFormGroup.addControl(
        "deaStartDate",
        this.formBuilder.control("")
      );
      this.innerFormGroup.addControl(
        "deaExpireDate",
        this.formBuilder.control("")
      );
      this.addDeaDatesSubscriptions();
    }
    if (!this.containsControl("taxId")) {
      this.innerFormGroup.addControl("taxId", this.formBuilder.control(""));
    }
    if (!this.containsControl("taxonomyCode")) {
      this.innerFormGroup.addControl(
        "taxonomyCode",
        this.formBuilder.control("")
      );
    }
    if (!this.containsControl("taxonomyDescription")) {
      this.innerFormGroup.addControl(
        "taxonomyDescription",
        this.formBuilder.control("")
      );
      this.subscriptions.push(
        this.resourceManagementService
          .loadTaxonomy()
          .pipe()
          .take(1)
          .subscribe((response) => {
            this.taxonomy = response.taxonomy;
            if (
              this.isEditMode &&
              this.innerFormGroup.get("taxonomyDescription")
            )
              this.innerFormGroup
                .get("taxonomyDescription")
                .setValue(this.taxonomyDescriptionByCode());
          })
      );
    }
  }

  private removeProviderControls() {
    if (this.containsControl("npi")) {
      this.removeControl("npi");
    }
    if (this.containsControl("dea")) {
      this.removeControl("dea");
      this.removeControl("deaStartDate");
      this.removeControl("deaExpireDate");
      this.removeDeaDateSubscriptions();
    }
    if (this.containsControl("taxId")) {
      this.removeControl("taxId");
    }
    if (this.containsControl("taxonomyDescription")) {
      this.removeControl("taxonomyDescription");
    }
    if (this.containsControl("taxonomyCode")) {
      this.removeControl("taxonomyCode");
    }
    this.npiDuplicated = false;
    this.npiShort = false;
  }

  private addDeaDatesSubscriptions() {
    this.providerSubscriptions.push(
      this.getDeaStartDate().valueChanges.subscribe((deaStartDate) =>
        this.validateDate(deaStartDate, "startDate")
      )
    );
    this.providerSubscriptions.push(
      this.getDeaExpireDate().valueChanges.subscribe((deaExpireDate) =>
        this.validateDate(deaExpireDate, "endDate")
      )
    );
  }

  private removeDeaDateSubscriptions() {
    this.providerSubscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }

  validateProviderAvailable(
    control: AbstractControl
  ): Observable<ValidationErrors | null> {
    return this.resourceManagementService
      .checkDuplicateProvider(control.value)
      .pipe(
        map((res) => {
          // check if self
          this.npiShort = false;
          if (this.contactData) {
            let p = this.contactData as ProfessionalProvider;
            if (control.value === p.npi) {
              return null;
            }
          }
          this.npiDuplicated = this.containsControl("npi") && res;
          return !res ? null : { npiTaken: true };
        })
      );
  }

  validateDate(date, field) {
    if (date) {
      if (field === "startDate") {
        const momentDate = moment(date);
        this.minDateValue = {
          year: momentDate.get("year"),
          month: momentDate.get("month") + 1,
          day: momentDate.date(),
        };
      }

      if (
        moment(this.getDeaStartDate().value).isAfter(
          moment(this.getDeaExpireDate().value)
        )
      ) {
        this.getDeaExpireDate().setValue(this.getDeaStartDate().value);
      }
    } else if (field === "startDate") {
      this.minDateValue = { year: 1900, month: 1, day: 1 };
    }
  }

  setTaxonomyValue(taxonomy): void {
    this.innerFormGroup.controls["taxonomyCode"].setValue(taxonomy.code);
    this.innerFormGroup.controls["taxonomyDescription"].setValue(
      taxonomy.code + " - " + taxonomy.description
    );
    this.taxonomyCodeDescription = [];
  }

  taxonomyChange(searchTerm: string): void {
    if (searchTerm.length >= 3) this.filterTaxonomy(searchTerm);
    else this.taxonomyCodeDescription = [];
  }

  clearTaxonomy(): void {
    this.innerFormGroup.controls["taxonomyDescription"].setValue(null);
    this.innerFormGroup.controls["taxonomyCode"].setValue(null);
    this.taxonomyCodeDescription = [];
  }

  filterTaxonomy(searchTerm: string): void {
    this.taxonomyCodeDescription = this.taxonomy.filter(
      (e) =>
        e.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }

  taxonomyDescriptionByCode(): string {
    const taxonomyCode: string =
      this.innerFormGroup.controls["taxonomyCode"].value;
    if (taxonomyCode && this.taxonomy.length > 0) {
      const taxonomy = this.taxonomy.find((e) => e.code === taxonomyCode);
      if (taxonomy) return taxonomy.code + " - " + taxonomy.description;
    }
    return "";
  }

  setNameTitle(): string {
    return ContactType.VOLUNTEERS === this.resourceType
      ? "Activity Type and Credentials"
      : "Licensing and Credentials";
  }
}



// WEBPACK FOOTER //
// ./src/components/system-admin/workforce/workforce-job-role-details/workforce-job-role-details.ts